import type { LocalAIState } from './useLocalAIStore'
import { AVAILABLE_MODELS, findModel, type ModelInfo } from './modelManager'

/** Modelo com pesos baixados no cache do navegador. */
export const isLocalModelDownloaded = (
  state: Pick<LocalAIState, 'modelSlots'>,
  modelId: string,
): boolean => state.modelSlots[modelId]?.downloaded === true

/** Motor WebLLM carregado e alinhado ao modelo selecionado. */
export const isLocalModelLoaded = (
  state: Pick<LocalAIState, 'status' | 'engine' | 'activeLoadedModelId' | 'selectedModelId'>,
): boolean =>
  state.status === 'ready' &&
  state.engine !== null &&
  state.activeLoadedModelId !== null &&
  state.activeLoadedModelId === state.selectedModelId &&
  state.selectedModelId !== null

/**
 * IA local pronta para chat no dispositivo: habilitada nas configurações,
 * modelo baixado e motor carregado.
 */
export const isLocalChatOperational = (state: LocalAIState): boolean =>
  state.isLocalEnabled && isLocalModelLoaded(state) && isLocalModelDownloaded(state, state.selectedModelId!)

/** Modelos do catálogo que estão baixados e carregados (ativos) nesta sessão. */
export const listActiveDownloadedModels = (
  state: Pick<LocalAIState, 'modelSlots' | 'status' | 'engine' | 'activeLoadedModelId'>,
): ModelInfo[] =>
  AVAILABLE_MODELS.filter(
    (model) =>
      isLocalModelDownloaded(state, model.id) &&
      state.status === 'ready' &&
      state.engine !== null &&
      state.activeLoadedModelId === model.id,
  )

export const formatLocalAiDownloadError = (err: unknown): string => {
  const raw = err instanceof Error ? err.message : String(err ?? '')
  const lower = raw.toLowerCase()

  if (/failed to fetch|networkerror|network error/i.test(lower)) {
    return (
      'Não foi possível baixar o modelo neste navegador. ' +
      'A IA local no dispositivo funciona melhor no celular (Chrome ou Safari, de preferência como app instalado) com WebGPU. ' +
      'No desktop, use a IA online ou teste no celular.'
    )
  }

  if (/webgpu|wasm threads|cross-origin|crossorigin/i.test(lower)) {
    return (
      'Este navegador não oferece WebGPU ou WASM com threads necessários para a IA local. ' +
      'Use um celular compatível ou a IA online.'
    )
  }

  if (findModel(raw)) return raw

  return raw.trim() || 'Falha ao baixar o modelo. Tente novamente.'
}
